import {
  type DirectoryPickerDirectoryHandle,
  type DirectoryPickerFileHandle,
  type DirectoryPickerHandle,
  getShowDirectoryPicker,
} from "../types/file-system-access";

export type DirectoryWalkEntryKind = "directory" | "file";

export interface DirectoryWalkEntry {
  kind: DirectoryWalkEntryKind;
  name: string;
  relativePath: string;
  sizeBytes?: number;
}

function joinPath(base: string, segment: string): string {
  const normalizedSegment = segment.replace(/\\/g, "/");
  if (!base) {
    return normalizedSegment;
  }
  const separator = base.includes("\\") ? "\\" : "/";
  const trimmed = base.replace(/[/\\]+$/, "");
  const parts = normalizedSegment.split("/").filter(Boolean);
  return [trimmed].concat(parts).join(separator);
}

function normalizeRelativePath(path: string): string {
  return path.replace(/\\/g, "/");
}

function inferRootFromFiles(files: File[]): string {
  if (files.length === 0) {
    return "";
  }
  const relativePath = normalizeRelativePath(files[0].webkitRelativePath || files[0].name);
  const firstSegment = relativePath.split("/").filter(Boolean)[0];
  return firstSegment ?? "";
}

/** Caps concurrent file-system reads so large folders don't open thousands of handles at once. */
const DEFAULT_SCAN_CONCURRENCY = 32;

/**
 * Lightweight FIFO semaphore. Used to bound the number of in-flight
 * directory listings and `getFile()` calls during a recursive scan.
 */
class Semaphore {
  private available: number;
  private readonly waiters: Array<() => void> = [];

  constructor(permits: number) {
    this.available = Math.max(1, permits);
  }

  async acquire(): Promise<void> {
    if (this.available > 0) {
      this.available -= 1;
      return;
    }
    await new Promise<void>((resolve) => this.waiters.push(resolve));
  }

  release(): void {
    const next = this.waiters.shift();
    if (next) {
      next();
      return;
    }
    this.available += 1;
  }
}

async function listImmediateEntries(
  dirHandle: DirectoryPickerDirectoryHandle
): Promise<Array<[string, DirectoryPickerHandle]>> {
  const items: Array<[string, DirectoryPickerHandle]> = [];
  const iterator = dirHandle.entries();
  let result = await iterator.next();
  while (!result.done) {
    items.push([result.value[0], result.value[1]]);
    result = await iterator.next();
  }
  return items;
}

/**
 * Walks a directory tree with bounded concurrency. Siblings are processed in
 * parallel while the shared semaphore caps simultaneous I/O. Output order is
 * not guaranteed, but downstream consumers sort/keys by path so it is fine.
 */
async function walkDirectoryHandle(
  dirHandle: DirectoryPickerDirectoryHandle,
  relativePath: string,
  semaphore: Semaphore,
  out: DirectoryWalkEntry[]
): Promise<void> {
  await semaphore.acquire();
  let items: Array<[string, DirectoryPickerHandle]>;
  try {
    items = await listImmediateEntries(dirHandle);
  } finally {
    semaphore.release();
  }

  await Promise.all(
    items.map(async ([name, handle]) => {
      const entryPath = relativePath ? `${relativePath}/${name}` : name;

      if (handle.kind === "directory") {
        out.push({ kind: "directory", name, relativePath: entryPath });
        await walkDirectoryHandle(
          handle as unknown as DirectoryPickerDirectoryHandle,
          entryPath,
          semaphore,
          out
        );
        return;
      }

      await semaphore.acquire();
      try {
        const file = await (handle as unknown as DirectoryPickerFileHandle).getFile();
        out.push({
          kind: "file",
          name,
          relativePath: entryPath,
          sizeBytes: file.size,
        });
      } finally {
        semaphore.release();
      }
    })
  );
}

export async function collectDirectoryHandleEntries(
  dirHandle: DirectoryPickerDirectoryHandle,
  relativePath = "",
  concurrency = DEFAULT_SCAN_CONCURRENCY
): Promise<DirectoryWalkEntry[]> {
  const entries: DirectoryWalkEntry[] = [];
  await walkDirectoryHandle(dirHandle, relativePath, new Semaphore(concurrency), entries);
  return entries;
}

export function* walkFileList(files: File[]): Generator<DirectoryWalkEntry> {
  const seenDirectories = new Set<string>();

  for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
    const file = files[fileIndex];
    const relativePath = normalizeRelativePath(file.webkitRelativePath || file.name);
    const segments = relativePath.split("/").filter(Boolean);

    let accumulated = "";
    for (let segmentIndex = 0; segmentIndex < segments.length - 1; segmentIndex += 1) {
      const segment = segments[segmentIndex];
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      if (seenDirectories.has(accumulated)) {
        continue;
      }
      seenDirectories.add(accumulated);
      yield {
        kind: "directory",
        name: segment,
        relativePath: accumulated,
      };
    }

    const fileName = segments[segments.length - 1] ?? file.name;
    yield {
      kind: "file",
      name: fileName,
      relativePath,
      sizeBytes: file.size,
    };
  }
}

export function collectSyncWalkEntries(generator: Generator<DirectoryWalkEntry>): DirectoryWalkEntry[] {
  const entries: DirectoryWalkEntry[] = [];
  let result = generator.next();
  while (!result.done) {
    entries.push(result.value);
    result = generator.next();
  }
  return entries;
}

export function prefixWalkEntries(
  entries: DirectoryWalkEntry[],
  rootPath: string
): DirectoryWalkEntry[] {
  const trimmedRoot = rootPath.trim();
  if (!trimmedRoot) {
    return entries;
  }

  return entries.map((entry) => ({
    kind: entry.kind,
    name: entry.name,
    sizeBytes: entry.sizeBytes,
    relativePath: joinPath(trimmedRoot, entry.relativePath),
  }));
}

export function isDirectoryPickerSupported(): boolean {
  return typeof getShowDirectoryPicker() === "function";
}

export async function pickDirectoryHandle(): Promise<DirectoryPickerDirectoryHandle> {
  const showDirectoryPicker = getShowDirectoryPicker();
  if (!showDirectoryPicker) {
    throw new Error("Folder picker is not supported in this browser. Try Chrome or Edge.");
  }
  return showDirectoryPicker();
}

export function pickDirectoryFilesViaInput(): Promise<File[]> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.setAttribute("webkitdirectory", "");
    input.setAttribute("directory", "");

    let settled = false;

    const finish = (files: File[]) => {
      if (settled) {
        return;
      }
      settled = true;
      if (files.length === 0) {
        reject(new DOMException("No folder was selected.", "AbortError"));
        return;
      }
      resolve(files);
    };

    input.addEventListener("change", () => {
      finish(Array.from(input.files ?? []));
    });

    input.addEventListener("cancel", () => {
      if (!settled) {
        settled = true;
        reject(new DOMException("Folder selection was cancelled.", "AbortError"));
      }
    });

    input.click();
  });
}

export interface ScanDirectoryResult {
  entries: DirectoryWalkEntry[];
  resolvedRootPath: string;
}

/** Opens exactly one folder picker and walks the selected tree. */
export async function scanDirectoryFromPicker(pathPrefix = ""): Promise<ScanDirectoryResult> {
  const trimmedPrefix = pathPrefix.trim();

  if (isDirectoryPickerSupported()) {
    const dirHandle = await pickDirectoryHandle();

    if (typeof dirHandle.requestPermission === "function") {
      const permission = await dirHandle.requestPermission({ mode: "read" });
      if (permission === "denied") {
        throw new Error("Read permission was denied for the selected folder.");
      }
    }

    const entries = await collectDirectoryHandleEntries(dirHandle);
    const resolvedRoot = trimmedPrefix || dirHandle.name;
    return {
      entries: prefixWalkEntries(entries, resolvedRoot),
      resolvedRootPath: resolvedRoot,
    };
  }

  const files = await pickDirectoryFilesViaInput();
  const entries = collectSyncWalkEntries(walkFileList(files));
  const resolvedRoot = trimmedPrefix || inferRootFromFiles(files);

  return {
    entries: prefixWalkEntries(entries, resolvedRoot),
    resolvedRootPath: resolvedRoot,
  };
}
